import * as React from 'react';
import { Attribute } from '../../../Model/Attribute';
import { ProductInterface } from '../../../Model/Product';
import { AttributeUtility } from '../../../Service/AttributeUtility';
import { ObjectUtility } from '../../../Service/ObjectUtility';
import { Trans } from '../../Trans';
import { AbstractRow } from '../AbstractRow';
import { AttributeCell } from '../AttributeCell';
import { FinderImage } from '../FinderImage';
import { HeadlineCell } from '../HeadlineCell';
import { MiscCell } from '../MiscCell';
import { WatchlistCell } from '../WatchlistCell';
import { ForceSensorHelper } from './ForceSensorHelper';
import { NominalForceItem } from './NominalForceItem';

function noWrap(attribute: Attribute) {
    return attribute.displayString.replace(/ /g, '\u00a0');
}

export class ForceSensorRow extends AbstractRow {
    public renderCells() {
        const product = this.props.product;

        return <>
            <td><FinderImage attr="Produktgruppenbild" product={product}/></td>
            <AttributeCell product={product} attr="Produktgruppenbeschreibung_kurz"/>
            <AttributeCell product={product} attr="sizes">
                <h4><Trans>Baugrösse</Trans></h4>
                {this.collectSizes(product)}
            </AttributeCell>
            <AttributeCell product={product} attr="Kundennutzen"/>
            <AttributeCell product={product} attr="Lagerung_Welle" extra={true}/>
            <AttributeCell product={product} attr="Montageart" extra={true}/>
            <AttributeCell product={product}
                           attr="Nennkraft"
                           extra={true}>{() => this.renderPowerCell(product)}</AttributeCell>
            <AttributeCell product={product}
                           attr="Lagerinnendurchmesser"
                           extra={true}>{() => this.renderAxisDiameterCell(product)}</AttributeCell>
            <AttributeCell product={product}
                           attr="Walzendurchmesser_MW"
                           extra={true}>{() => this.renderRollerDiameterCell(product)}</AttributeCell>
            <AttributeCell product={product}
                           attr="Walzenlaenge_MW"
                           extra={true}>{() => this.renderRollerLengthCell(product)}</AttributeCell>
            <MiscCell product={product}
                      manualAttr={'Montageanweisung'}
                      manualTitle={'Montageanweisung'}
                      calculator={true}/>
        </>;
    }

    protected renderPowerCell(product: ProductInterface) {
        return <><h4><Trans>Nennkraft</Trans></h4>{this.collectPower(product).join(', ')}</>;
    }

    protected renderAxisDiameterCell(product: ProductInterface) {
        return <><h4><Trans>Lagerinnendurchmesser</Trans></h4>{this.collectAxisDiameter(product).join(', ')}</>;
    }

    protected renderRollerDiameterCell(product: ProductInterface) {
        return <><h4><Trans>Walzendurchmesser</Trans></h4>{this.collectRollerDiameter(product).join(', ')}</>;
    }

    protected renderRollerLengthCell(product: ProductInterface) {
        return <><h4><Trans>Walzenlänge</Trans></h4>{this.collectRollerLength(product).join(', ')}</>;
    }

    protected renderHeadline(product: ProductInterface): JSX.Element {
        const suffix = this.getNameSuffix(product);

        return <>
            <HeadlineCell colSpan={4} product={product} attr="name" suffix={suffix}/>
            <WatchlistCell product={product}/>
        </>;
    }

    protected collectSizes(product: ProductInterface) {
        if (!product.children) {
            return [];
        }
        const products = product.children.sort((a, b) => {
            if (a.prio === b.prio) {
                return 0;
            }
            return a.prio > b.prio ? 1 : -1;
        });
        const lastIndex = products.length - 1;

        return products.map(
            (child, i) => <NominalForceItem key={child.guid} product={child} isLast={i === lastIndex}/>
        );
    }

    protected collectPower(product: ProductInterface) {
        if (!product.children) {
            return [];
        }

        return ObjectUtility.unique(
            AttributeUtility.sortByNumericValue(
                AttributeUtility.childAttributesForKeyFiltered(product.children, 'Nennkraft')
            ).map(noWrap)
        );
    }

    protected collectAxisDiameter(product: ProductInterface) {
        return AttributeUtility.attributesForKeyFiltered(product.children, 'Lagerinnendurchmesser')
            .map(a => a.displayString);
    }

    protected collectRollerDiameter(product: ProductInterface) {
        return ForceSensorHelper.rollerDiameterCollectAttributesOfProduct(product)
            .map(noWrap);
    }

    protected collectRollerLength(product: ProductInterface) {
        return ForceSensorHelper.rollerLengthCollectAttributesOfProduct(product)
            .map(noWrap);
    }

    private getNameSuffix(product: ProductInterface): string {
        const defaultId = 'products.force_sensor.search_result_suffix';
        const isRMGZ = /rmgz/i.test(product.name);
        if (!isRMGZ) {
            return defaultId;
        }

        const attribute: Attribute | undefined = ObjectUtility.valueForKeyPath(product, 'Lagerung_Welle');
        const displayString = attribute ? attribute.displayString : '';
        if (displayString === 'Seilrolle' || displayString === 'Pulley/sheave') {
            return 'products.force_sensor.search_result_suffix.pulley';
        } else {
            return defaultId;
        }
    }
}
